import { getStartupConfig, useStartupConfig } from '@/features/home/api/getStartupConfig';
import { useAuthStore } from '@/stores/authStore';
import { fetchAuthSession, getCurrentUser, signOut } from 'aws-amplify/auth';
import { useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router';
import { FullPageSpinner } from '../FullPageSpinner/FullPageSpinner';

export const AuthBoundary = () => {
  const { status, setStatus, login, updateUserData } = useAuthStore();
  const location = useLocation();

  const { refetch } = useStartupConfig({ enabled: false });

  const hasJustAuthenticated = useRef(false);

  useEffect(() => {
    if (status === 'AUTHENTICATED') {
      hasJustAuthenticated.current = true;
      return;
    }
  }, [status]);

  useEffect(() => {
    if (hasJustAuthenticated.current) {
      hasJustAuthenticated.current = false;
      return;
    }

    if (status === 'AUTHENTICATED') {
      void refetch().then((res) => {
        if (!res.data) {
          return;
        }

        updateUserData({
          ...res.data.data,
          creditCardExpiriesAt: res.data.data.creditCardExpiriesAt ? new Date(res.data.data.creditCardExpiriesAt) : undefined,
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        setStatus('LOADING');

        const session = await fetchAuthSession({ forceRefresh: true });
        if (!session.tokens?.idToken) {
          setStatus('UNAUTHENTICATED');
          return;
        }

        let cognitoUser;
        try {
          cognitoUser = await getCurrentUser();
        } catch {
          cognitoUser = null;
        }

        const startupConfig = await getStartupConfig();

        if (!cognitoUser) {
          setStatus('UNAUTHENTICATED');
          await signOut();
          return;
        }

        login({
          cognitoUser,
          center: startupConfig.data.center,
          guardian: startupConfig.data.guardian,
          students: startupConfig.data.students,
          creditCardExpiriesAt: startupConfig.data.creditCardExpiriesAt ? new Date(startupConfig.data.creditCardExpiriesAt) : undefined,
          failedPayment: startupConfig.data.failedPayment,
          overdueInvoice: startupConfig.data.overdueInvoice,
          isDemoEligible: startupConfig.data.isDemoEligible,
          ketchToken: startupConfig.data.ketchToken,
        });
      } catch {
        await signOut();
        setStatus('UNAUTHENTICATED');
      }
    };

    void fetchUser();
  }, [setStatus, login]);

  if (['IDLE', 'LOADING'].includes(status)) {
    return <FullPageSpinner />;
  }

  return <Outlet />;
};
